import { useEffect, useRef, useState } from "react";
import { Box } from "components";
import useIOSDetection from "hooks/useIOSDetection";
import useScanAnalytics from "hooks/useScanAnalytics";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import { useLocation } from "react-router-dom";
import { useValidatePassportMutation } from "redux/queries/passport.query";
import { useGetSettingsQuery } from "redux/queries/settings.query";

import LanguageDrawer from "./flightList/LanguageDrawer";
import Body from "./scanPassport/Body";
import CircleStyled from "./scanPassport/CircleStyled";
import Header from "./scanPassport/Header";
import OCRScanner from "./scanPassport/OCRScanner";
import PreCameraDrawer from "./scanPassport/PreCameraDrawer";
import STEPS from "./scanPassport/Steps";
import ValidationDrawer from "./scanPassport/ValidationDrawer";

export default function ScanPassport() {
  const { sendAnalytics } = useTrackPageAnalytics();
  const { data: settingsData = {} } = useGetSettingsQuery(undefined, {
    refetchOnReconnect: true,
  });

  const { featureMap: featureFlags } = settingsData;
  const areAnalyticsEnabled = !featureFlags?.bypass_passport_scanning_analytics;
  const scanAnalytics = useScanAnalytics(areAnalyticsEnabled);
  const iosDetection = useIOSDetection();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const source = params.get("source"); // 'qr' or 'click'

  useTrackPageAnalytics(
    source === "qr"
      ? {
          key: "navigate-smart-gate-from-qr",
          name: "Accessed Smart Gate from QR",
        }
      : {
          key: "navigate-smart-gate-click",
          name: "Accessed Smart Gate from Main Menu click",
        }
  );

  useEffect(() => {
    scanAnalytics.markFeatureEntry();
    // Set OS info in analytics
    let osString = "unknown";
    if (iosDetection.isMacOS) osString = "macos";
    if (iosDetection.isMobileSafari) osString = "ios_mobile_safari";
    else if (iosDetection.isSafari) osString = "safari";
    else if (iosDetection.isChrome) osString = "chrome";
    scanAnalytics.setOS(
      osString + (iosDetection.iOSVersion ? `_${iosDetection.iOSVersion}` : "")
    );
  }, [scanAnalytics, iosDetection]);

  const [currentStep, setCurrentStep] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validatePassport, { isLoading: isValidating }] =
    useValidatePassportMutation();
  const [scannerKey, setScannerKey] = useState(0);
  const [passportData, setPassportData] = useState(null);
  const [showPreCameraDrawer, setShowPreCameraDrawer] = useState(false);

  const pauseScanner = useRef(false);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      return;
    }
    if (areAnalyticsEnabled) {
      setShowPreCameraDrawer(true);
    } else {
      setShowScanner(true);
    }
  };

  const handleSkip = () => {
    if (areAnalyticsEnabled) {
      setShowPreCameraDrawer(true);
    } else {
      setShowScanner(true);
    }
  };

  const handleScanComplete = async (ocrData) => {
    if (pauseScanner.current) {
      return;
    }
    pauseScanner.current = true;
    const ocrFields = ocrData.parsed.fields;

    sendAnalytics({
      key: "smart-gate-successful-scan",
      type: "event",
      name: "Successfully scanned and parsed the passport",
    });

    const passportData = {
      passportNo: ocrFields.documentNumber,
      passportNationality: ocrFields.issuingState,
      genderId: ocrFields.sex,
      dateOfBirth: ocrFields.birthDate,
    };

    setPassportData(passportData);

    // Set gender in analytics
    scanAnalytics.setGender(ocrFields.sex);

    // Set date of birth in analytics
    scanAnalytics.setDateOfBirth(ocrFields.birthDate);

    setIsDrawerOpen(true);

    try {
      const response = await validatePassport(passportData).unwrap();

      setValidationResult(response);
    } catch (error) {
      setValidationResult({
        status: "error",
        message:
          error?.message || "Unable to validate passport. Please try again.",
      });
    }
  };

  const handleClose = () => {
    scanAnalytics.sendAnalyticsIfNeeded({
      reason: "user_exit",
      detail: "user_exited_feature",
    });
    sendAnalytics({
      key: "smart-gate-click-close",
      type: "event",
      name: "User clicked on 'X' button and was redirected to main page",
    });
    // TODO: Do a better handling for handling the analytics
    setTimeout(() => {
      window.location.href = "/";
    }, 200);
  };

  const handleDrawerClose = () => {
    setValidationResult(null);
    setIsDrawerOpen(false);
    setShowScanner(true);
    setScannerKey((prev) => prev + 1);
    pauseScanner.current = false;
  };

  const handlePreCameraContinue = () => {
    setShowPreCameraDrawer(false);
    setShowScanner(true);
  };

  const handlePreCameraCancel = () => {
    window.location.href = "/";
  };

  if (showPreCameraDrawer) {
    if (!areAnalyticsEnabled) {
      setShowPreCameraDrawer(false);
      setShowScanner(true);
      return null;
    }
    return (
      <PreCameraDrawer
        isOpen={showPreCameraDrawer}
        onContinue={handlePreCameraContinue}
        onCancel={handlePreCameraCancel}
        message="Please get ready to scan your passport. Make sure your document is visible and well-lit."
      />
    );
  }

  if (showScanner) {
    return (
      <>
        <OCRScanner
          pauseScanner={pauseScanner}
          key={scannerKey}
          onScanComplete={handleScanComplete}
          onClose={handleClose}
          scanAnalytics={scanAnalytics}
        />
        <ValidationDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          validationResult={validationResult}
          isValidating={isValidating}
          onContinue={handleClose}
          scanAnalytics={scanAnalytics}
          nationality={passportData?.passportNationality}
          gender={passportData?.genderId}
          dateOfBirth={passportData?.dateOfBirth}
        />
      </>
    );
  }

  return (
    <>
      <Box
        style={{
          height: "100svh",
          backgroundColor: "#797FD6",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CircleStyled circlePosition={STEPS[currentStep].circlePosition} />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Header />
          <Body
            content={STEPS[currentStep]}
            currentStep={currentStep}
            numberOfSteps={STEPS.length}
            onNext={handleNext}
            onSkip={handleSkip}
          />
        </div>
        <LanguageDrawer />
      </Box>
    </>
  );
}
